// Open an intent from the user's wallet (docs/builders-ui.md §2–6). The open builds exactly the quote
// the user confirmed. A solver's quote is live for `valid_for_ms` (a few seconds) and `/build`
// refuses a stale one; only then is a fresh quote taken, and it replaces the confirmed one without
// asking only if it guarantees at least as much (`PriceChanged` otherwise). The api's authorisation
// lapses at the build's `open_by` (seconds; per source chain): an open that waited past it (an approval, a slow wallet)
// is built again, under the same rule, rather than sent to revert.
// A non-stable input goes through the api's hosted swap (§4): one router call on EVM, the
// Jupiter route composed with the open on Solana; the escrow, and any refund, is the stable.
// Jupnet escrows one of its stables (no swap there); the api co-signs and pays for the open, and
// the user's Jupnet key (`jupnet-key.ts`) signs it.

import { Keypair, Transaction, VersionedTransaction } from "@solana/web3.js";
import { awaitRegistered, build, isPriceMoved, firstValue, intentsOf, pickBest, quoteRound, registerOpen, submit, type Built, type Declined, type Delivery, type Quote, type QuoteHead } from "./api";
import { isNativeEvm, isStable, type Chain, type Token } from "./chains";
import {
  approveData,
  batchFallbackAllowed,
  batchIntentHash,
  ensureChain,
  erc20Allowance,
  erc20Balance,
  ethBalance,
  evmPath,
  openedIntentHash,
  permitCalldata,
  postedPermit,
  sendBatch,
  sendTx,
  signTypedData,
  simulate,
  waitReceipt,
  walletAtomic,
  type CallsStatus,
  type Eip1193,
  type EvmPath,
} from "./evm";
import { GUM_JUPNET_PROGRAM, checkJupnetOpen, jupnetSigning, recipientRaw, signJupnetOpen } from "./jupnet";
import { builtTxs, openedFromLogs, rowForOpenTx, txLogs } from "./solana";
import { guaranteed, notWorse } from "./terms";
import { fromBaseUnits } from "./validate";

type SolTx = Transaction | VersionedTransaction;

export type OpenParams = {
  api: string;
  src: Chain;
  dst: Chain;
  /** what the user pays: the chain's stable, or any token the api swaps into it */
  inputToken: Token;
  dstToken: Token;
  /** gross in `inputToken`, smallest units */
  amount: string;
  /** Jupnet, paid from several stablecoins (a USD basket): each and its gross, smallest units;
   *  `inputToken` / `amount` then describe the first */
  inputs?: { token: Token; amount: string }[];
  recipient: string;
  /** `gum_deposit`: `recipient` is a Jupnet account, credited through gum's bank on `dst` */
  delivery?: Delivery;
  slippageBps: number;
  /** the source swap's tolerance (hosted swap only; the api's default otherwise) */
  srcSlippageBps?: number;
  /** the source wallet's address (Jupnet: the Jupnet key's) */
  user: string;
  /** the quote round and solver quote the user confirmed: what the open builds */
  confirmed: { head: QuoteHead; quote: Quote };
  evm?: Eip1193;
  solana?: { signTransaction: <T extends SolTx>(tx: T) => Promise<T>; signAllTransactions?: <T extends SolTx>(txs: T[]) => Promise<T[]> };
  solanaRpc?: string;
  /** gum-intent's program id, whose log names a Solana open's intent (default GUM_PROGRAM) */
  solanaProgram?: string;
  /** the user's Jupnet key: signs a Jupnet open */
  jupnetKey?: Keypair;
  /** gum-intent's Jupnet program id, the only program a Jupnet open may call (default GUM_JUPNET_PROGRAM) */
  jupnetProgram?: string;
  /** the connected Sui wallet: signs the api's built open, returns `/submit`'s payload */
  sui?: { sign: (txBytesBase64: string) => Promise<string> };
  log: (msg: string) => void;
  /** where the open is, for the UI's button and stepper */
  phase?: (p: OpenPhase) => void;
};
/** `switch` the wallet's network, `build` the quote, `approve` the input (a transaction, a permit
 *  or a batch), `sign` the open, `submit` it, `confirm` it landed and find its intent. */
export type OpenPhase = "switch" | "build" | "approve" | "sign" | "submit" | "confirm";
export type Opened = { intentHash: string; openTx: string; quote: Quote; head: QuoteHead; built: Built; path?: EvmPath | "bundle" | "single" };

export const NO_QUOTE = "No solver quoted this pair; try another pair or amount.";
export const NO_SOLVER = "No solver is connected to the api right now, so nobody can quote. Try again shortly.";
/** Why a round brought no quote: nobody connected, the reasons solvers gave, or none given. */
export function noQuoteMessage(solversAsked: number, declined: Declined[] = []): string {
  if (solversAsked === 0) return NO_SOLVER;
  if (!declined.length) return NO_QUOTE;
  const why = declined.map((d) => (declined.length > 1 ? `${d.solver}: ${d.reason}` : d.reason)).join("; ");
  return `No solver quoted this pair. ${declined.length > 1 ? "Solvers said" : "The solver said"}: ${why}`;
}

/** What each EVM path means to the user, for the log. */
export const PATH_LABEL: Record<EvmPath, string> = {
  none: "no approval needed",
  batch: "approve + open in one wallet batch (EIP-5792)",
  permit: "gasless permit signature, then one transaction",
  approve: "approval transaction, then the open",
};

async function freshQuote(p: OpenParams): Promise<{ head: QuoteHead; best: Quote }> {
  const swap = !isStable(p.src, p.inputToken.addr);
  const { head, quotes, declined } = await quoteRound(p.api, {
    src_chain: p.src.key,
    dst_chain: p.dst.key,
    ...(p.inputs ? { inputs: p.inputs.map((i) => ({ token: i.token.addr, amount: i.amount })) } : { input_token: p.inputToken.addr, input_amount: p.amount }),
    dst_token: p.dstToken.addr,
    dst_recipient: p.recipient,
    ...(swap && p.srcSlippageBps !== undefined ? { src_slippage_bps: p.srcSlippageBps } : {}),
    ...(p.delivery === "gum_deposit" ? { delivery: p.delivery } : {}),
  });
  const best = pickBest(quotes);
  if (!best) throw new Error(noQuoteMessage(head.solvers_asked, declined));
  return { head, best };
}

/** Everything an open is built from, as one comparable value: a quote round is only usable for
 *  the form it was taken for. */
export function formKey(f: { src: string; dst: string; inputToken: string; amount: string | null; srcSlippage?: number; dstToken: string; recipient: string; delivery?: Delivery; slippage: number }): string {
  return JSON.stringify([f.src, f.dst, f.inputToken, f.amount, f.srcSlippage ?? null, f.dstToken, f.recipient, f.delivery ?? "direct", f.slippage]);
}

/** Whether a quote round taken for the form `taken` may be shown (or opened) for the form as it is
 *  `now`: only for the very same inputs. A replacement that arrives after the user edited the form
 *  (e.g. a PriceChanged quote while they typed a new recipient) is dropped. */
export const quoteFitsForm = (taken: string, now: string): boolean => taken === now;

export type QuoteState = { head: QuoteHead; quotes: Quote[]; done: boolean; key: string };

/** What a PriceChanged open leaves on screen: the fresh quote, if the form is still the one the
 *  open was taken for (`taken` vs `current`), else the form's own state (`kept`). */
export function afterPriceChanged(e: PriceChanged, taken: string, current: string, kept: QuoteState | null): QuoteState | null {
  return quoteFitsForm(taken, current) ? { head: e.fresh.head, quotes: [e.fresh.quote], done: true, key: taken } : kept;
}

/** The quote on screen after a finished round. A background refresh that brought no quote keeps
 *  the previous one (its error is shown, and the next refresh is scheduled); a round the user asked
 *  for shows what it got, empty or not. */
export function afterRound(prev: QuoteState | null, round: QuoteState | null, refresh: boolean): QuoteState | null {
  if (refresh && (!round || !round.quotes.length) && prev?.quotes.length) return prev;
  return round;
}

/** What the user had already done when a price change stopped the open: nothing, an approval
 *  transaction confirmed on chain (it stays), a permit signed (never sent, so unused), or an open
 *  signed too late to send (never sent either). */
export type SignedBefore = "nothing" | "approval" | "permit" | "open";

/** The confirmed quote can no longer be built and a fresh one guarantees less: show `fresh` and
 *  let the user decide. No open was signed; `before` says what was. */
export class PriceChanged extends Error {
  constructor(
    readonly was: bigint,
    readonly now: bigint,
    readonly fresh: { head: QuoteHead; quote: Quote },
    readonly before: SignedBefore = "nothing",
  ) {
    super("the price changed since you confirmed");
  }
}

/** The sentence telling the user what a `PriceChanged` left behind. */
export function priceChangedNote(e: PriceChanged): string {
  switch (e.before) {
    case "approval":
      return "No swap was opened; your approval stays, so swapping again needs none.";
    case "permit":
      return "No swap was opened; the permit you signed was not used, so swapping again asks for a new one.";
    case "open":
      return "The open you signed was not sent: nothing moved.";
    default:
      return "Nothing was signed.";
  }
}

/** Seconds an open must still have before its `open_by` to be put in front of the wallet: less, and
 *  it is built again (a signature landing after `open_by` reverts). */
export const OPEN_BY_MARGIN_S = 10;

/** An EVM source's margin: a wallet confirmation and two blocks (about 12 s each on Ethereum,
 *  a second or two elsewhere), at least a quarter of the window the build came with, at least
 *  `OPEN_BY_MARGIN_S`: an EVM open that lands late reverts at the user's gas. */
export function evmMarginS(src: Pick<Chain, "key">, built: Pick<Built, "open_within_secs">): number {
  const block = src.key === "ethereum" ? 12 : 2;
  return Math.max(OPEN_BY_MARGIN_S, 2 * block + 15, Math.floor((built.open_within_secs ?? 0) / 4));
}
/** The same, once signed: what is left must cover `/submit` and landing. */
const SUBMIT_MARGIN_S = 2;

/** Whether `built` is too close to (or past) its deadline to sign now: on this device's clock
 *  (`open_by_local_ms`, from the api's `open_within_secs`), else `open_by` against it. */
export function openLapsing(built: Pick<Built, "open_by" | "open_by_local_ms">, nowMs = Date.now(), marginS = OPEN_BY_MARGIN_S): boolean {
  if (built.open_by_local_ms !== undefined) return nowMs > built.open_by_local_ms - marginS * 1000;
  return built.open_by !== undefined && nowMs / 1000 > built.open_by - marginS;
}

class RequoteNeeded extends Error {}

/** A build refused because the confirmed quote lapsed or the swap's price moved. */
const lapsed = (e: unknown) => isPriceMoved(e) || /no live quote/i.test(String((e as Error)?.message));

/** Build the quote the user confirmed. If it lapsed, a fresh quote is built instead only if it
 *  guarantees at least as much; otherwise `PriceChanged`, before anything is signed. */
async function buildConfirmed(p: OpenParams, o: { requote?: boolean; before?: SignedBefore } = {}): Promise<{ head: QuoteHead; best: Quote; built: Built }> {
  p.phase?.("build");
  const want = guaranteed(p.confirmed.head, p.confirmed.quote, p.slippageBps);
  const buildOf = (head: QuoteHead, q: Quote) => build(p.api, { quote_id: head.quote_id, solver: q.solver, dst_amount: q.dst_amount, slippage_bps: p.slippageBps, user: p.user });
  let head = p.confirmed.head;
  let best = p.confirmed.quote;
  let built: Built;
  try {
    // `requote`: the confirmed quote is long gone (an approval took the window): straight to a
    // fresh one, under the same rule
    if (o.requote) throw new RequoteNeeded();
    built = await buildOf(head, best);
  } catch (e) {
    if (!(e instanceof RequoteNeeded) && !lapsed(e)) throw e;
    p.log(isPriceMoved(e) ? "the swap's price moved since you confirmed; checking a fresh quote…" : "the quote you confirmed lapsed; checking a fresh one…");
    const fresh = await freshQuote(p);
    const got = guaranteed(fresh.head, fresh.best, p.slippageBps);
    if (!notWorse(want, got)) throw new PriceChanged(want, got, { head: fresh.head, quote: fresh.best }, o.before);
    p.log(`a fresh quote guarantees at least ${fromBaseUnits(got.toString(), p.dstToken.decimals)} ${p.dstToken.sym}, not less than you confirmed: continuing with it`);
    ({ head, best } = fresh);
    built = await buildOf(head, best);
  }
  if (built.tx.vm !== p.src.family) throw new Error(`the api built a ${built.tx.vm} transaction for a ${p.src.label} source`);
  // the signed delivery decides where the output goes: never sign one the user did not choose
  if ((built.args?.delivery ?? 0) !== (p.delivery === "gum_deposit" ? 1 : 0)) throw new Error(`the api built delivery ${built.args?.delivery} for a ${p.delivery ?? "direct"} open; nothing was signed`);
  p.log(`quoted by ${best.solver}: ${fromBaseUnits(best.dst_amount, p.dstToken.decimals)} ${p.dstToken.sym}, floor ${fromBaseUnits(built.min_dst_amount, p.dstToken.decimals)}`);
  if (head.source_swap) p.log(`swapping ${fromBaseUnits(p.amount, p.inputToken.decimals)} ${p.inputToken.sym} → at least ${fromBaseUnits(built.source_swap?.min_out ?? head.source_swap.min_out, p.src.stable.decimals)} ${p.src.stable.sym} (${head.source_swap.aggregator})`);
  return { head, best, built };
}

export async function openIntent(p: OpenParams): Promise<Opened> {
  if (p.src.family === "evm") return openEvm(p);
  if (p.src.family === "solana") return openSolana(p);
  if (p.src.family === "sui") return openSui(p);
  if (p.src.family === "jupnet") return openJupnet(p);
  throw new Error(`${p.src.label} is not supported as a source yet`);
}

async function openEvm(p: OpenParams): Promise<Opened> {
  const w = p.evm;
  if (!w) throw new Error("connect an EVM wallet first");
  const chainId = p.src.evmChainId!;
  p.phase?.("switch");
  await ensureChain(w, chainId, p.src.label, p.src.addChain);
  const native = p.inputToken.native || isNativeEvm(p.inputToken.addr);
  const bal = native ? await ethBalance(w, p.user) : await erc20Balance(w, p.inputToken.addr, p.user);
  if (bal < BigInt(p.amount)) throw new Error(`insufficient ${p.inputToken.sym}: you have ${fromBaseUnits(bal, p.inputToken.decimals)} on ${p.src.label}`);

  let { head, best, built } = await buildConfirmed(p);
  if (!head.source_swap) return openEvmStable(p, w, head, best, built);

  // hosted swap: the router pulls the token, so its approval (if any) is of the router
  let path = evmPath(built, built.approval ? await walletAtomic(w, p.user, chainId) : false);
  if (path === "batch" && openLapsing(built, Date.now(), evmMarginS(p.src, built))) {
    // the wallet's capability check took the window: the batch would carry a lapsing open. Built
    // again, once (nothing is signed yet); after the wallet takes a batch, never again
    p.log("too little of the open's authorisation is left to land it; building it again…");
    ({ head, best, built } = await buildConfirmed(p, { requote: true }));
    if (openLapsing(built, Date.now(), evmMarginS(p.src, built))) throw new Error("the open's authorisation would lapse before it lands; open again");
    if (!built.approval) path = "none";
  }
  p.log(`approval: ${PATH_LABEL[path]}`);
  const evmTx = (b: Built) => {
    if (b.tx.vm !== "evm") throw new Error("not an EVM build");
    return { to: b.tx.to, data: b.tx.data, value: BigInt(b.tx.value) };
  };

  if (path === "batch") {
    let st: CallsStatus | null = null;
    try {
      p.log("confirm approve + open in your wallet…");
      p.phase?.("approve");
      st = await sendBatch(w, p.user, chainId, [{ to: built.approval!.tx.to, data: built.approval!.tx.data }, evmTx(built)]);
    } catch (e: any) {
      // once the wallet took the batch it may still open: never send a second one
      if (!batchFallbackAllowed(e)) throw e;
      // a wallet that announced the capability and then refused the batch: fall through
      path = built.permit ? "permit" : "approve";
      p.log(`the wallet did not take the batch (${e?.message ?? e}); falling back to: ${PATH_LABEL[path]}`);
    }
    if (st) {
      // the receipts carry the router's event; else the api finds the open from the transaction
      const intentHash = batchIntentHash(st.receipts, evmTx(built).to) ?? (st.txHash ? await awaitRegistered(p.api, p.src.key, st.txHash) : null);
      if (!intentHash) throw new Error(`the batch confirmed but its open was not found${st.txHash ? ` (${st.txHash})` : ""}; look the transaction up rather than opening again`);
      if (st.txHash) void registerOpen(p.api, p.src.key, st.txHash).catch(() => null);
      return { intentHash, openTx: st.txHash ?? "", quote: best, head, built, path };
    }
  }
  let open = evmTx(built);
  let permit: ReturnType<typeof postedPermit> | null = null;
  if (path === "permit") {
    p.log("sign the permit in your wallet (no gas)…");
    p.phase?.("approve");
    permit = postedPermit(built.permit!, await signTypedData(w, p.user, built.permit!.typed_data));
    // the permit variant of the very call that was built: same swap, same terms, same quote
    open = { ...open, data: permitCalldata(open.data, permit) };
  } else if (path === "approve") {
    const a = built.approval!;
    p.log(`approve ${fromBaseUnits(a.amount, p.inputToken.decimals)} ${p.inputToken.sym} for the swap router in your wallet…`);
    p.phase?.("approve");
    const ah = await sendTx(w, p.user, { to: a.tx.to, data: a.tx.data }, "approval");
    p.log(`approval sent (${ah}); waiting for it to confirm…`);
    await waitReceipt(w, ah);
    // then the open that was built while it is in time (if the swap's price moved out of the band
    // meanwhile, the simulation below says so before sending)
  }
  let sent = { head, best, built };
  // an approval or permit may have taken the authorisation's window: built again then (the
  // permit, if any, still holds), checked right before the wallet is asked
  const h = await sendOpen(p, w, sent.built, open, async () => {
    sent = await buildConfirmed(p, { requote: true, before: path === "approve" ? "approval" : path === "permit" ? "permit" : "nothing" });
    open = evmTx(sent.built);
    if (permit) open = { ...open, data: permitCalldata(open.data, permit) };
    return { built: sent.built, open };
  });
  const intentHash = await intentOf(p, w, h, open.to);
  if (!intentHash) throw new Error(`no SwapOpened event in ${h}`);
  return { intentHash, openTx: h, quote: sent.best, head: sent.head, built: sent.built, path };
}

/** The stable itself: approve the proxy (`tx.to`) if short, then open. */
async function openEvmStable(p: OpenParams, w: Eip1193, head: QuoteHead, best: Quote, built: Built): Promise<Opened> {
  if (built.tx.vm !== "evm") throw new Error("not an EVM build");
  let tx = built.tx;
  const allowance = await erc20Allowance(w, p.src.stable.addr, p.user, tx.to);
  const approved = allowance < BigInt(p.amount);
  if (approved) {
    p.log(`approve ${fromBaseUnits(p.amount, p.src.stable.decimals)} ${p.src.stable.sym} in your wallet…`);
    p.phase?.("approve");
    const ah = await sendTx(w, p.user, { to: p.src.stable.addr, data: approveData(tx.to, BigInt(p.amount)) }, "approval");
    p.log(`approval sent (${ah}); waiting for it to confirm…`);
    await waitReceipt(w, ah);
  }
  const h = await sendOpen(p, w, built, { to: tx.to, data: tx.data, value: BigInt(tx.value) }, async () => {
    ({ head, best, built } = await buildConfirmed(p, { requote: true, before: approved ? "approval" : "nothing" }));
    if (built.tx.vm !== "evm") throw new Error("not an EVM build");
    tx = built.tx;
    return { built, open: { to: tx.to, data: tx.data, value: BigInt(tx.value) } };
  });
  const intentHash = await intentOf(p, w, h, tx.to);
  if (!intentHash) throw new Error(`no Opened event in ${h}`);
  return { intentHash, openTx: h, quote: best, head, built };
}

/** The api re-reads the open from chain as soon as it lands (`POST /intents`); the receipt's event
 *  is the fallback. Whichever names the intent first starts the tracker. */
function intentOf(p: OpenParams, w: Eip1193, h: `0x${string}`, to: string): Promise<string | null> {
  const viaApi = awaitRegistered(p.api, p.src.key, h);
  const viaReceipt = waitReceipt(w, h).then((rc) => openedIntentHash(rc, to));
  return firstValue([viaApi, viaReceipt]);
}

type EvmOpen = { to: string; data: `0x${string}`; value: bigint };

/** Simulate and send `open`, unless its window has less than the source's margin left (checked
 *  again right before the wallet is asked): then `rebuild` it, once. */
async function sendOpen(p: OpenParams, w: Eip1193, built: Built, open: EvmOpen, rebuild: () => Promise<{ built: Built; open: EvmOpen }>): Promise<`0x${string}`> {
  // one rebuild allowed, however many checks it takes (a simulation can cross the margin too)
  for (let rebuilt = false; ; ) {
    if (openLapsing(built, Date.now(), evmMarginS(p.src, built))) {
      if (rebuilt) throw new Error("the open's authorisation would lapse before it lands; open again");
      p.log("too little of the open's authorisation is left to land it; building it again…");
      ({ built, open } = await rebuild());
      rebuilt = true;
      continue;
    }
    try {
      await simulate(w, p.user, open);
    } catch (e: any) {
      throw new Error(`the open would revert (${e?.message ?? e}); get a new quote and retry`);
    }
    if (!openLapsing(built, Date.now(), evmMarginS(p.src, built))) break;
  }
  p.log("confirm the open in your wallet…");
  p.phase?.("sign");
  const h = await sendTx(w, p.user, open, "open");
  p.log(`open sent (${h}); waiting for it to land…`);
  p.phase?.("confirm");
  return h;
}

const fromB64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const toB64 = (b: Uint8Array) => btoa(Array.from(b, (x) => String.fromCharCode(x)).join(""));

async function openSolana(p: OpenParams): Promise<Opened> {
  const w = p.solana;
  if (!w) throw new Error("connect a Solana wallet first");
  // signed past its `open_by`, the open would only fail: built and signed again, once
  for (let attempt = 0; ; attempt++) {
    // a retry follows an open the user signed but that was not sent: a fresh quote, same rule
    const { head, best, built } = await buildConfirmed(p, attempt > 0 ? { requote: true, before: "open" } : {});
    if (built.tx.vm !== "solana") throw new Error("not a Solana build");

    // the plain open: the api's Ed25519 authorisation, then open_intent (legacy). A hosted swap: v0
    // transaction(s) with Jupiter's route in front. The user is fee payer and only signer of each.
    const plan = builtTxs(built.tx);
    const txs: SolTx[] = plan.txs.map((b) => (plan.versioned ? VersionedTransaction.deserialize(fromB64(b)) : Transaction.from(fromB64(b))));
    if (plan.bundle && !w.signAllTransactions) throw new Error("this swap needs two transactions signed together, and your wallet cannot sign several at once");
    p.log(plan.bundle ? "sign the swap and the open (2 transactions, landed together) in your wallet…" : "sign the open in your wallet…");
    p.phase?.("sign");
    let signed: SolTx[];
    try {
      signed = plan.bundle ? await w.signAllTransactions!(txs) : [await w.signTransaction(txs[0]!)];
    } catch (e: any) {
      if (/reject|denied|cancel/i.test(String(e?.message ?? e))) throw new Error("you rejected the open in your wallet");
      throw new Error(`signing: ${e?.message ?? e}`);
    }
    if (openLapsing(built, Date.now(), SUBMIT_MARGIN_S)) {
      if (attempt > 0) throw new Error("the open's authorisation lapsed before it was signed; open again");
      p.log("the open's authorisation lapsed while signing; building it again…");
      continue;
    }
    return submitSolana(p, plan, signed, head, best, built);
  }
}

async function submitSolana(p: OpenParams, plan: ReturnType<typeof builtTxs>, signed: SolTx[], head: QuoteHead, best: Quote, built: Built): Promise<Opened> {
  const b64s = signed.map((t) => toB64(t.serialize()));
  p.phase?.("submit");
  const { tx: sig, intentHash: submitted } = await submit(p.api, "solana", plan.bundle ? b64s : b64s[0]!);
  const path = plan.versioned ? (plan.bundle ? "bundle" : "single") : undefined;
  // the api re-read the confirmed open and named its intent: track it now
  if (submitted) return { intentHash: submitted, openTx: sig, quote: best, head, built, path };
  p.log(`open sent (${sig}); finding its intent…`);
  p.phase?.("confirm");

  // the program's log has the hash at once; the api has it once it registers the open
  for (let i = 0; i < 90; i++) {
    const reg = await registerOpen(p.api, "solana", sig).catch(() => null);
    if (reg?.kind === "indexed") return { intentHash: reg.intentHash, openTx: sig, quote: best, head, built, path };
    const logs = await txLogs(p.solanaRpc, sig);
    const ev = logs && openedFromLogs(logs, p.solanaProgram);
    if (ev) return { intentHash: ev.intentHash, openTx: sig, quote: best, head, built, path };
    const row = rowForOpenTx(await intentsOf(p.api, p.user, "solana").catch(() => []), sig);
    if (row) return { intentHash: row.intent_hash, openTx: sig, quote: best, head, built, path };
    await new Promise((r) => setTimeout(r, 2000));
  }
  throw new Error(`could not find the intent for ${sig}; it may still land, check the explorer and track it by hash`);
}

/** Sui: the wallet signs the open the api built (sender, gas and commands already set) and the api
 *  executes it; `/submit` names the intent once its short wait sees the open, else `POST /intents`. */
async function openSui(p: OpenParams): Promise<Opened> {
  const w = p.sui;
  if (!w) throw new Error("connect a Sui wallet first");
  if (!isStable(p.src, p.inputToken.addr)) throw new Error(`${p.src.label} takes ${p.src.stable.sym} only: the api has no hosted swap there`);
  let head: QuoteHead, best: Quote, built: Built, payload: string;
  // signed past its `open_by`, the open would only fail: built and signed again, once
  for (let attempt = 0; ; attempt++) {
    // a retry follows an open the user signed but that was not sent: a fresh quote, same rule
    ({ head, best, built } = await buildConfirmed(p, attempt > 0 ? { requote: true, before: "open" } : {}));
    if (built.tx.vm !== "sui") throw new Error("not a Sui build");
    p.log("sign the open in your wallet…");
    p.phase?.("sign");
    try {
      payload = await w.sign(built.tx.tx_bytes_base64);
    } catch (e: any) {
      if (/reject|denied|cancel/i.test(String(e?.message ?? e))) throw new Error("you rejected the open in your wallet");
      throw new Error(`signing: ${e?.message ?? e}`);
    }
    if (!openLapsing(built, Date.now(), SUBMIT_MARGIN_S)) break;
    if (attempt > 0) throw new Error("the open's authorisation lapsed before it was signed; open again");
    p.log("the open's authorisation lapsed while signing; building it again…");
  }
  p.phase?.("submit");
  const { tx: digest, intentHash: submitted } = await submit(p.api, "sui", payload);
  if (submitted) return { intentHash: submitted, openTx: digest, quote: best, head, built };
  p.log(`open sent (${digest}); finding its intent…`);
  p.phase?.("confirm");
  const intentHash = await awaitRegistered(p.api, "sui", digest);
  if (!intentHash) throw new Error(`could not find the intent for ${digest}; it may still land, check the explorer and track it by hash`);
  return { intentHash, openTx: digest, quote: best, head, built };
}

/** Jupnet: the api built the open; it signs as fee payer and `opener` at `/submit`. The user's
 *  Jupnet key signs its message as it is; the signature goes into the user's slot, is checked over
 *  the api's exact message, and the api co-signs and broadcasts the whole. */
async function openJupnet(p: OpenParams): Promise<Opened> {
  const key = p.jupnetKey;
  if (!key) throw new Error("set up a Jupnet key first: it owns the escrow and signs the open");
  if ((p.inputs?.map((i) => i.token) ?? [p.inputToken]).some((t) => !isStable(p.src, t.addr))) throw new Error(`${p.src.label} takes its stablecoins only: the api has no hosted swap there`);
  const { head, best, built } = await buildConfirmed(p);
  if (built.tx.vm !== "jupnet") throw new Error("not a Jupnet build");
  // the user's key signs these bytes with no wallet to show them: hold them against the request
  checkJupnetOpen(fromB64(built.tx.message_base64), {
    program: p.jupnetProgram ?? GUM_JUPNET_PROGRAM,
    user: p.user,
    inputMint: p.inputToken.addr,
    amount: p.amount,
    minGross: head.min_gross ?? head.gross,
    maxGross: head.max_gross ?? head.gross,
    dstChainId: p.dst.gumChainId,
    dstToken: p.dstToken.addr,
    dstFamily: p.dst.family,
    dstRecipient: recipientRaw(p.delivery === "gum_deposit" ? "jupnet" : p.dst.family, p.recipient),
    delivery: p.delivery === "gum_deposit" ? 1 : 0,
    rateNum: best.dst_amount,
    rateDen: head.net,
    slippageBps: p.slippageBps,
    validitySeconds: head.validity_seconds,
    builderFeeBps: head.builder_fee_bps,
    protocolFeeBps: head.protocol_fee_bps,
    exclusiveMs: best.exclusive_ms ?? 0,
    ...(p.inputs ? { inputs: p.inputs.map((i) => ({ mint: i.token.addr, amount: i.amount })) } : {}),
  });
  p.log("signing the open with your Jupnet key (the api pays its fee)…");
  p.phase?.("sign");
  const signed = signJupnetOpen(jupnetSigning(built.tx, p.user), key);
  p.phase?.("submit");
  const { tx: sig, intentHash: submitted } = await submit(p.api, p.src.key, signed);
  if (submitted) return { intentHash: submitted, openTx: sig, quote: best, head, built };
  p.log(`open sent (${sig}); finding its intent…`);
  p.phase?.("confirm");
  const intentHash = await awaitRegistered(p.api, p.src.key, sig);
  if (!intentHash) throw new Error(`could not find the intent for ${sig}; it may still land, track it by hash once it shows`);
  return { intentHash, openTx: sig, quote: best, head, built };
}
